/*
 * Birthday offer — floating badge, gift picker popup, cart drawer picker and the product page "Birthday gifts" block.
 *
 * There are two gift groups, each with its own qualifying tag and gift products: "maps" (the popup and badge) and
 * "kids". Cart state per group (qualifying items / gifts / chosen gifts) comes from [data-bday-state="<group>"]
 * rendered by snippets/birthday-offer-cart.liquid, which is part of the cart-drawer section the theme already returns
 * on every cart change. Gifts are tracked by product ([data-bday-gift] = product id, [data-bday-group] = its group);
 * gifts with variants have a [data-bday-variant] dropdown. "maps" below means the group's qualifying items.
 *
 * Choosing a gift ([data-bday-choose]), within the gift's group:
 * - a free slot is left (more maps than gifts) → the gift is added
 * - every map already has its gift → swap: one unit of the previously chosen gift is replaced by this one
 * - the gift is already chosen → nothing, or removed when the button has [data-bday-toggle] (product page block)
 * - no map and no gift in the cart → the gift is pre-selected and added right after the next map is added
 */
(() => {
  if (window.customElements.get("birthday-offer")) {
    return;
  }

  const routesRoot = () => window.Shopify?.routes?.root || "/";

  // Translated labels, read from [data-bday-strings] (locales/*.json "birthday_offer")
  let strings = {
    addFree: "Add free",
    selected: "✓ Selected",
    swap: "Swap",
    giftOne: "1 gift",
    giftOther: "__count__ gifts",
    unavailable: "This gift is not available right now."
  };

  const postJson = async (url, body) => {
    const response = await fetch(`${routesRoot()}${url}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(body)
    });
    const json = await response.json();

    if (!response.ok) {
      throw new Error(json.description || json.message || strings.unavailable);
    }

    return json;
  };

  const fetchCart = async () => (await fetch(`${routesRoot()}cart.js`)).json();

  // Id of the cart drawer section as the theme bundles it: "cart-drawer" for a static section, or the generated id
  // ("sections--123__cart-drawer") when the drawer lives in a section group, as in older Impact versions
  const drawerSectionId = () => {
    const section = document.getElementById("cart-drawer")?.closest(".shopify-section");
    return section ? section.id.replace("shopify-section-", "") : "cart-drawer";
  };

  const groupOf = (element) => element?.dataset.bdayGroup || "maps";

  const emptyGroup = () => ({ maps: 0, gifts: 0, chosen: new Map() });

  class BirthdayOffer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector(".bday-modal");

      try {
        strings = { ...strings, ...JSON.parse(this.querySelector("[data-bday-strings]")?.textContent || "{}") };
      } catch (error) {
        // Keep the English fallbacks
      }

      this.groups = {
        maps: { ...emptyGroup(), maps: parseInt(this.dataset.maps, 10) || 0, gifts: parseInt(this.dataset.gifts, 10) || 0 }
      };
      this.pending = null;
      this.busy = false;

      this._onClick = this._onClick.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onCartChange = this._onCartChange.bind(this);
      this._onCartRefresh = this._onCartRefresh.bind(this);

      document.addEventListener("click", this._onClick);
      document.addEventListener("change", this._onVariantChange);
      document.addEventListener("cart:change", this._onCartChange);
      document.addEventListener("cart:refresh", this._onCartRefresh);

      this.dialog.addEventListener("click", (event) => {
        // Click on the backdrop closes the popup
        if (event.target === this.dialog) {
          this.close();
        }
      });

      this._insertProductBanner();

      if (document.querySelector("#cart-drawer [data-bday-state]")) {
        this._readState(document);
      } else if (this.groups.maps.gifts > 0 || document.querySelector('[data-bday-group="kids"]')) {
        this._onCartRefresh();
      } else {
        this._render();
      }
    }

    disconnectedCallback() {
      document.removeEventListener("click", this._onClick);
      document.removeEventListener("change", this._onVariantChange);
      document.removeEventListener("cart:change", this._onCartChange);
      document.removeEventListener("cart:refresh", this._onCartRefresh);
    }

    _group(name) {
      return this.groups[name] || emptyGroup();
    }

    _remaining(name) {
      const group = this._group(name);
      return Math.max(group.maps - group.gifts, 0);
    }

    _state(name) {
      if (this._group(name).maps === 0) {
        return "intro";
      }
      return this._remaining(name) > 0 ? "choose" : "done";
    }

    open() {
      this._setError("");
      if (!this.dialog.open) {
        this.dialog.showModal();
      }
    }

    close() {
      if (this.dialog.open) {
        this.dialog.close();
      }
    }

    _render() {
      // The popup and the badge belong to the maps group
      const state = this._state("maps");
      const remaining = this._remaining("maps");
      this.dataset.state = state;

      this.querySelectorAll("[data-bday-remaining]").forEach((el) => {
        el.textContent = remaining <= 1 ? strings.giftOne : strings.giftOther.replace("__count__", remaining);
      });

      const count = this.querySelector("[data-bday-count]");
      if (count) {
        count.hidden = state !== "choose";
        count.textContent = remaining;
      }

      this.querySelector(".bday-badge")?.classList.toggle("bday-badge--pulse", state === "choose");
      // Gift cards and rows everywhere (popup, cart drawer, product page block)
      document.querySelectorAll("[data-bday-gift]").forEach((gift) => {
        const productId = gift.dataset.bdayGift;
        const group = groupOf(gift);
        const isChosen = this._group(group).chosen.has(productId);

        gift.classList.toggle("is-chosen", isChosen);
        gift.classList.toggle("is-selected", this.pending?.productId === productId);

        const cardButton = gift.querySelector(".bday-card__add[data-bday-choose]");
        if (cardButton) {
          cardButton.textContent = isChosen ? strings.selected : (this._remaining(group) > 0 ? strings.addFree : strings.swap);
        }
      });

      // Product page "Birthday gifts" block (snippets/birthday-gifts-block.liquid)
      document.querySelectorAll(".bday-gifts").forEach((block) => {
        const group = groupOf(block);
        const blockState = this._state(group);
        block.dataset.state = this.pending?.group === group && blockState === "intro" ? "selected" : blockState;

        block.querySelectorAll("[data-bday-selected-title]").forEach((el) => {
          el.textContent = this.pending?.group === group ? this.pending.title : "";
        });
      });
    }

    _onClick(event) {
      const target = event.target.closest("[data-bday-open], [data-bday-close], [data-bday-choose], [data-bday-cart]");
      if (!target) {
        return;
      }

      if (target.hasAttribute("data-bday-open")) {
        this.open();
      } else if (target.hasAttribute("data-bday-close")) {
        this.close();
      } else if (target.hasAttribute("data-bday-cart")) {
        this.close();
        document.getElementById("cart-drawer")?.show?.();
      } else if (target.hasAttribute("data-bday-choose")) {
        this._choose(target);
      }
    }

    // Selected variant of a gift card / row: its dropdown when the gift has variants, otherwise the button default
    _variantOf(gift, button) {
      return gift?.querySelector("[data-bday-variant]")?.value || button?.dataset.bdayChoose;
    }

    _choose(button) {
      if (this.busy) {
        return;
      }

      const gift = button.closest("[data-bday-gift]");
      const productId = gift.dataset.bdayGift;
      const group = groupOf(gift);
      const variantId = this._variantOf(gift, button);
      const { maps, gifts, chosen } = this._group(group);

      if (maps === 0 && gifts === 0) {
        const title = gift.querySelector(".complementary-product__title, .bday-card__title")?.textContent.trim();
        this.pending = this.pending?.productId === productId ? null : { productId, variantId, title, group };
        this._render();
        return;
      }

      this.pending = null;

      if (chosen.has(productId)) {
        if (button.hasAttribute("data-bday-toggle")) {
          this._run(button, () => this._removeOne(productId));
        }
      } else if (this._remaining(group) > 0) {
        this._run(button, () => this._add(variantId));
      } else {
        this._run(button, () => this._swap(productId, variantId, group));
      }
    }

    // Changing the variant of a chosen gift swaps it in the cart; of a pre-selected gift, updates the selection
    _onVariantChange(event) {
      const select = event.target.closest("[data-bday-variant]");
      const gift = select?.closest("[data-bday-gift]");
      const productId = gift?.dataset.bdayGift;

      if (!productId) {
        return;
      }

      if (this.pending?.productId === productId) {
        this.pending.variantId = select.value;
      } else if (this._group(groupOf(gift)).chosen.has(productId) && !this.busy) {
        this._run(select, () => this._swapVariant(productId, select.value));
      }
    }

    async _add(variantId) {
      const json = await postJson("cart/add.js", {
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: drawerSectionId()
      });
      return json.sections;
    }

    async _removeLine(line) {
      const json = await postJson("cart/change.js", {
        id: line.key,
        quantity: line.quantity - 1,
        sections: drawerSectionId()
      });
      return json.sections;
    }

    async _removeOne(productId) {
      const cart = await fetchCart();
      const line = cart.items.find((item) => String(item.product_id) === productId);

      return line ? this._removeLine(line) : null;
    }

    async _swap(productId, variantId, group) {
      const cart = await fetchCart();
      const { chosen } = this._group(group);
      // cart.items lists the most recently added lines first, so the last chosen gift of this group is the one replaced
      const previous = cart.items.find((item) => chosen.has(String(item.product_id)) && String(item.product_id) !== productId);

      if (previous) {
        await this._removeLine(previous);
      }

      return this._add(variantId);
    }

    async _swapVariant(productId, variantId) {
      const cart = await fetchCart();
      const previous = cart.items.find((item) => String(item.product_id) === productId && String(item.variant_id) !== variantId);

      if (!previous) {
        return null;
      }

      await this._removeLine(previous);
      return this._add(variantId);
    }

    async _run(button, task) {
      this.busy = true;
      this._setError("");

      document.querySelectorAll("[data-bday-choose], [data-bday-variant]").forEach((el) => el.setAttribute("disabled", "disabled"));
      button?.setAttribute("aria-busy", "true");

      const fromPopup = Boolean(button?.matches("[data-bday-choose]") && this.dialog.contains(button));

      try {
        const sections = await task();
        const cart = await fetchCart();

        if (sections) {
          cart.sections = sections;
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", cart }
        }));

        if (fromPopup && this._remaining("maps") === 0) {
          this.close();
          document.getElementById("cart-drawer")?.show?.();
        }
      } catch (error) {
        this._setError(error.message);
      } finally {
        this.busy = false;
        button?.removeAttribute("aria-busy");
        document.querySelectorAll("[data-bday-choose], [data-bday-variant]").forEach((el) => el.removeAttribute("disabled"));
      }
    }

    _onCartChange(event) {
      const cart = event.detail?.cart;
      const sectionHtml = cart?.sections?.[drawerSectionId()];

      if (sectionHtml) {
        this._readState(new DOMParser().parseFromString(sectionHtml, "text/html"));
      } else if (cart && cart.item_count === 0) {
        this._readState(null);
      } else {
        this._onCartRefresh();
        return;
      }

      // A qualifying item was just added while a gift of its group was pre-selected on the product page: add that gift now
      if (this.pending && event.detail?.baseEvent === "variant:add" && this._remaining(this.pending.group) > 0 && !this.busy) {
        const { variantId } = this.pending;
        this.pending = null;
        this._render();
        this._run(null, () => this._add(variantId));
      }
    }

    async _onCartRefresh() {
      const html = await (await fetch(`${routesRoot()}?section_id=cart-drawer`)).text();
      this._readState(new DOMParser().parseFromString(html, "text/html"));
    }

    _readState(doc) {
      this.groups = {};

      doc?.querySelectorAll("[data-bday-state]").forEach((stateElement) => {
        this.groups[stateElement.dataset.bdayState || "maps"] = {
          maps: parseInt(stateElement.dataset.maps, 10) || 0,
          gifts: parseInt(stateElement.dataset.gifts, 10) || 0,
          chosen: new Map(
            (stateElement.dataset.chosen || "")
              .split(",")
              .filter(Boolean)
              .map((pair) => {
                const [productId, quantity] = pair.split(":");
                return [productId, parseInt(quantity, 10)];
              })
          )
        };
      });

      this._render();
    }

    _setError(message) {
      document.querySelectorAll("[data-bday-error]").forEach((error) => {
        error.hidden = !message;
        error.textContent = message;
      });
    }

    _insertProductBanner() {
      const template = this.querySelector("template[data-bday-pdp]");
      const buyButtons = document.querySelector("main buy-buttons");

      // The "Birthday gifts" product block already tells the story, no need for the banner too
      if (template && buyButtons && !document.querySelector(".bday-gifts")) {
        buyButtons.after(template.content.cloneNode(true));
      }
    }
  }

  window.customElements.define("birthday-offer", BirthdayOffer);
})();
